import React, { useState, useCallback } from "react";
import useSWR from "swr";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import CloseIcon from "@mui/icons-material/Close";
import Checkbox from "@mui/material/Checkbox";

import { useNotification } from "../../Notifications/NotificationProvider";
import ApproveIcon from "../icons/approve.svg";
import RejectIcon from "../icons/reject.svg";
import { fetcher } from "../../../utilities/_fetcher";
import { getLoanConfirmationUi, successStatuses } from "../helper/loanHelper";
import {
  getReviewSubmissionReasonsUrl,
  submitLoanReviewSubmission,
} from "../service/loanService";

const LoanConfirmationModal = ({ loan, action, onClose, onConfirm }) => {
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [reasons, setReasons] = useState([]);
  const dispatchNotification = useNotification();

  const isRecommendation = action === "recommend";
  const isRetryAction = action === "retry";
  const reasonType = isRecommendation ? "accept" : "reject";
  const apiUserType = "om";
  const { title, message, buttonText, buttonClass } =
    getLoanConfirmationUi(action);

  const { data: reasonsResponse, error } = useSWR(
    isRetryAction
      ? null
      : getReviewSubmissionReasonsUrl({
          userType: apiUserType,
          reasonType,
        }),
    fetcher,
    {
      fallbackData: {
        reasons: [],
      },
      onSuccess: (data) => {
        setReasons(data.reasons || []);
      },
      onError: (error) => {
        setReasons([]);
      },
    }
  );

  const loading = !isRetryAction && !reasonsResponse && !error;

  const handleReasonToggle = useCallback((reason) => {
    setSelectedReasons((prev) =>
      prev.includes(reason)
        ? prev.filter((r) => r !== reason)
        : [...prev, reason]
    );
  }, []);

  const completeSuccessfulAction = () => {
    dispatchNotification({
      type: "SUCCESS",
      message: isRetryAction
        ? "Loan retried successfully"
        : `Loan ${action === "recommend" ? "recommended" : "rejected"} successfully`,
    });

    onConfirm(action, selectedReasons);
    onClose();
  };

  const handleSubmit = async () => {
    if (!isRetryAction && selectedReasons.length === 0) {
      dispatchNotification({
        type: "ERROR",
        message: "Please select at least one reason",
      });
      return;
    }

    try {
      setSubmitting(true);
      await submitLoanReviewSubmission({
        identityId: loan.identity_id,
        loanRequestId: loan.loan_request_id,
        userType: apiUserType,
        reasonType: isRetryAction ? null : reasonType,
        reasons: isRetryAction ? null : selectedReasons,
      });

      completeSuccessfulAction();
    } catch (error) {
      const responseStatus = error?.response?.data?.status;

      if (successStatuses.includes(responseStatus)) {
        completeSuccessfulAction();
        return;
      }

      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to submit. Please try again.";

      dispatchNotification({
        type: "ERROR",
        message: errorMessage,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="approval-modal-overlay">
      <div className="loan-confirmation-modal">
        <div className="loan-confirmation-modal__header">
          <div className="loan-confirmation-modal__header-left">
            <button
              className="loan-confirmation-modal__back"
              onClick={onClose}
              disabled={submitting}
            >
              <ArrowBackIosIcon fontSize="small" />
            </button>
            <h2
              className={`loan-confirmation-modal__title ${
                isRecommendation
                  ? "loan-confirmation-modal__title--green"
                  : "loan-confirmation-modal__title--red"
              }`}
            >
              {title}
            </h2>
          </div>
          <button
            className="loan-confirmation-modal__close"
            onClick={onClose}
            disabled={submitting}
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="loan-confirmation-modal__content">
          <p className="loan-confirmation-modal__message">{message}</p>

          {loading ? (
            <div className="loan-confirmation-modal__loading">
              {[...Array(5)].map((_, index) => (
                <div key={index} className="reason-skeleton" />
              ))}
            </div>
          ) : reasons.length > 0 ? (
            <div className="loan-confirmation-modal__reasons">
              {reasons.map((reason) => (
                <label key={reason} className="reason-checkbox">
                  <span className="reason-checkbox__text">{reason}</span>
                  <Checkbox
                    checked={selectedReasons.includes(reason)}
                    onChange={() => handleReasonToggle(reason)}
                    disabled={submitting}
                    sx={{
                      color: "#e2136e",
                      "&.Mui-checked": {
                        color: "#e2136e",
                      },
                    }}
                  />
                </label>
              ))}
            </div>
          ) : null}
        </div>

        <div className="loan-confirmation-modal__footer">
          <button
            className={`btn ${buttonClass}`}
            onClick={handleSubmit}
            disabled={
              (!isRetryAction && selectedReasons.length === 0) ||
              submitting ||
              loading
            }
          >
            <img
              src={isRecommendation || isRetryAction ? ApproveIcon : RejectIcon}
              alt={buttonText}
              className="btn--icon"
            />
            {submitting ? "Processing..." : buttonText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoanConfirmationModal;
